'use client';
/**
 * search_unify1 s-ui — 「{지역} 인기 단지」 섹션(홈: 대표 캐러셀 바로 아래 · 지역 기억 따라감 / 허브: 경로 지역 / 상세: 지역 고정).
 *   h2 20/800 + 「전체 보기 ›」 · note(계약 label 그대로 — 「최근 7일 관심 기준 · 매일 갱신」 | 「일정 임박 단지 · 매일 갱신」)
 *   · 세그먼트 2칸(인기 단지 / 인기 검색어 · 선택 navy) · 시군구 탭 칩(선택 navy · thin 탭 숨김 — rpc get_popular_sigungu_tabs)
 *   · 행 58(PopularSiteList) · 하단 44 「{지역} 인기 단지 전체 보기」.
 * 서버 HTML = 없음(지역은 브라우저 기억에만 있다 — 방문자 무관 HTML). 플래그 ui.popular_region off · 데이터 없음 = 섹션째 생략.
 */
import React from 'react'; // vitest(클래식 JSX) 렌더 검증용
import { useEffect, useState } from 'react';
import { popularArgs, searchHref, type PopularRegionData } from '@/lib/search/pop-region';
import { fetchPopularRegion, usePopularRegion, usePopularTabs } from '@/lib/search/use-pop-region';
import PopularSiteList from './PopularSiteList';
import { useScopeNode } from './HomeHeroChips';

export type PopularVariant = 'section' | 'side' | 'miss';

export function PopularRegionView({ data, moreHref, tabs = [], tab = null, onTab, variant = 'section', limit, src = 'hero' }: {
  data: PopularRegionData; moreHref: string | null; tabs?: string[]; tab?: string | null; onTab?: (t: string | null) => void;
  /** side = 상세 사이드 「이 지역 인기 단지」 5 · miss = /search 0건 화면 3 · section = 홈/허브 */
  variant?: PopularVariant;
  limit?: number;
  src?: string;
}) {
  const [seg, setSeg] = useState<'site' | 'keyword'>('site');
  const label = data.label || '전국';
  if (!data.sites.length && !data.keywords.length) return null;
  const full = variant === 'section';
  const segOn = !full || !data.keywords.length ? 'site' : !data.sites.length ? 'keyword' : seg;
  if (segOn === 'site' && !data.sites.length) return null;
  const n = limit ?? (variant === 'side' ? 5 : variant === 'miss' ? 3 : 10);
  return (
    <section className={`kd-popsec kd-popsec--${variant}`} aria-label={`${label} 인기 단지`} data-popular-region={variant}>
      <div className="kd-popsec__head">
        {variant === 'side' ? <h3>이 지역 인기 단지</h3> : variant === 'miss' ? <h3>{label} 인기 단지</h3> : <h2>{label} 인기 단지</h2>}
        {moreHref && full ? <a href={moreHref}>{`${label} 인기 단지 전체 보기`} ›</a> : null /* 레인 J ④ — 뜻 없는 「전체 보기」 단독 금지 */}
      </div>
      <p className="kd-popsec__note">{data.note}</p>
      {full && data.sites.length && data.keywords.length ? (
        <div className="kd-popsec__seg" role="group" aria-label="인기 목록 종류">
          <button type="button" aria-pressed={segOn === 'site'} onClick={() => setSeg('site')}>인기 단지</button>
          <button type="button" aria-pressed={segOn === 'keyword'} onClick={() => setSeg('keyword')}>인기 검색어</button>
        </div>
      ) : null}
      {full && segOn === 'site' && tabs.length > 1 && onTab ? (
        <ul className="kd-popsec__tabs" aria-label="시군구">
          <li><button type="button" aria-pressed={!tab} onClick={() => onTab(null)}>전체</button></li>
          {tabs.map((t) => <li key={t}><button type="button" aria-pressed={tab === t} onClick={() => onTab(t)}>{t}</button></li>)}
        </ul>
      ) : null}
      <div className="kd-popsec__box">
        {segOn === 'site' ? (
          <PopularSiteList sites={data.sites} label={label} limit={n} thumbs={variant === 'section'} />
        ) : (
          <ol className="kd-gs__kw">
            {data.keywords.map((k) => (
              <li key={k.keyword}><a className="kd-gs__kwa" href={searchHref(k.keyword, src)}><b>{k.rank}</b> <span>{k.label}</span></a></li>
            ))}
          </ol>
        )}
      </div>
      {moreHref && segOn === 'site' && full ? <a className="kd-popsec__all" href={moreHref}>{label} 인기 단지 전체 보기</a> : null}
    </section>
  );
}

export default function PopularRegionSection({ fixedRegion = null, fixedSigungu = null, moreHref = null, variant = 'section', src = 'hero', wrap = false, excludeSlug = null }: {
  fixedRegion?: string | null; fixedSigungu?: string | null; moreHref?: string | null; variant?: PopularVariant; src?: string;
  /** 홈·허브 — ds2-wrap 폭 안에 둔다(데이터 있을 때만 생김). */
  wrap?: boolean;
  /** 상세 — 보고 있는 현장은 목록에서 뺀다(계약 exclude). */
  excludeSlug?: string | null;
}) {
  const node = useScopeNode();
  const base = fixedRegion ? { region: fixedRegion, sigungu: fixedSigungu } : popularArgs(node);
  const lim = variant === 'side' ? 5 : variant === 'miss' ? 3 : 10;
  const [tab, setTab] = useState<string | null>(null);
  const top = usePopularRegion(base.region, base.sigungu, true, { limit: lim, exclude: excludeSlug });
  const tabs = usePopularTabs(variant === 'section' && !base.sigungu ? base.region : null);
  const [sub, setSub] = useState<PopularRegionData | null>(null);
  useEffect(() => { setTab(null); setSub(null); }, [base.region, base.sigungu]);
  useEffect(() => {
    if (!tab || !base.region) { setSub(null); return; }
    let alive = true;
    void fetchPopularRegion(base.region, tab, { limit: lim }).then((d) => { if (alive) setSub(d); });
    return () => { alive = false; };
  }, [tab, base.region, lim]);
  if (!top) return null;
  const shown = tab && sub && sub.sites.length ? sub : top;
  if (!shown.sites.length && !shown.keywords.length) return null;
  const href = moreHref ?? (fixedRegion ? null : node?.href ?? null);
  const view = <PopularRegionView data={shown} moreHref={href} tabs={tabs} tab={tab} onTab={setTab} variant={variant} limit={lim} src={src} />;
  return wrap ? <div className="ds2-wrap">{view}</div> : view;
}
